import { Box, Flex, Input, Text, useColorModeValue } from '@chakra-ui/react'
import { Check } from 'lucide-react'
import React, { useEffect, useRef } from 'react'
import { useLanguage } from '../../hooks/utility/useLanguage'
import { ClarificationOption } from '../../types/chatTypes'
import { Selection } from './selection'

interface OptionListProps {
  choices: ClarificationOption[]
  escape?: ClarificationOption
  isMulti: boolean
  selection: Selection
  focusedRow: number
  disabled: boolean
  onTogglePick: (label: string) => void
  onSetSelection: (next: Selection) => void
}

export const OptionList: React.FC<OptionListProps> = ({
  choices,
  escape,
  isMulti,
  selection,
  focusedRow,
  disabled,
  onTogglePick,
  onSetSelection,
}) => {
  const { t } = useLanguage()
  const isPicked = (label: string) =>
    selection.kind === 'pick' && selection.values.includes(label)

  return (
    <Box>
      {choices.map((opt, i) => {
        const label = opt.label ?? ''
        return (
          <OptionRow
            key={label}
            label={label}
            isMulti={isMulti}
            isSelected={isPicked(label)}
            isFocused={focusedRow === i}
            disabled={disabled}
            onClick={() => onTogglePick(label)}
          />
        )
      })}
      {escape && (
        <EscapeHatchRow
          label={t('clarification.escapeHatchLabel')}
          isMulti={isMulti}
          selection={selection}
          isFocused={focusedRow === choices.length}
          disabled={disabled}
          onChange={onSetSelection}
        />
      )}
    </Box>
  )
}

interface OptionRowProps {
  label: string
  isMulti: boolean
  isSelected: boolean
  isFocused: boolean
  disabled: boolean
  onClick: () => void
}

const OptionRow: React.FC<OptionRowProps> = ({
  label,
  isMulti,
  isSelected,
  isFocused,
  disabled,
  onClick,
}) => {
  const { focusBg, selectedBg } = useRowColors()
  const bg = isSelected ? selectedBg : isFocused ? focusBg : 'transparent'

  return (
    <Flex
      align='center'
      gap={3}
      px={5}
      py={3}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      bg={bg}
      _hover={disabled ? undefined : { bg: isSelected ? selectedBg : focusBg }}
      onClick={() => {
        if (!disabled) onClick()
      }}
    >
      <SelectionIndicator isMulti={isMulti} isSelected={isSelected} />
      <Text>{label}</Text>
    </Flex>
  )
}

interface EscapeHatchRowProps {
  label: string
  isMulti: boolean
  selection: Selection
  isFocused: boolean
  disabled: boolean
  onChange: (next: Selection) => void
}

const EscapeHatchRow: React.FC<EscapeHatchRowProps> = ({
  label,
  isMulti,
  selection,
  isFocused,
  disabled,
  onChange,
}) => {
  const { focusBg, selectedBg } = useRowColors()
  const placeholderColor = useColorModeValue('gray.500', 'gray.400')
  const isActive = selection.kind === 'custom'
  const text = selection.kind === 'custom' ? selection.text : ''
  const inputRef = useRef<HTMLInputElement>(null)
  const { t } = useLanguage()

  // Pull focus into the text input the moment this row activates (typically
  // via Enter on the focused escape-hatch row).
  useEffect(() => {
    if (isActive) inputRef.current?.focus()
  }, [isActive])

  const bg = isActive ? selectedBg : isFocused ? focusBg : 'transparent'

  return (
    <Flex
      align='center'
      gap={3}
      px={5}
      py={3}
      bg={bg}
      _hover={disabled ? undefined : { bg: isActive ? selectedBg : focusBg }}
    >
      <Box
        as='button'
        type='button'
        aria-label={isActive ? t('clarification.deselectOption', { label }) : t('clarification.selectOption', { label })}
        display='flex'
        alignItems='center'
        bg='transparent'
        border='none'
        p={0}
        cursor={disabled ? 'not-allowed' : 'pointer'}
        onClick={() => {
          if (disabled) return
          onChange(isActive ? { kind: 'none' } : { kind: 'custom', text: '' })
        }}
      >
        <SelectionIndicator isMulti={isMulti} isSelected={isActive} />
      </Box>
      <Input
        ref={inputRef}
        variant='unstyled'
        flex={1}
        placeholder={label}
        _placeholder={{ color: placeholderColor }}
        value={text}
        isDisabled={disabled}
        onChange={e => onChange({ kind: 'custom', text: e.target.value })}
      />
    </Flex>
  )
}

const SelectionIndicator: React.FC<{
  isMulti: boolean
  isSelected: boolean
}> = ({ isMulti, isSelected }) => {
  const indicatorBorder = useColorModeValue('gray.300', 'gray.500')
  return (
    <Box
      w='18px'
      h='18px'
      borderRadius={isMulti ? 'sm' : 'full'}
      border='1.5px solid'
      borderColor={isSelected ? 'blue.500' : indicatorBorder}
      bg={isSelected ? 'blue.500' : 'transparent'}
      display='flex'
      alignItems='center'
      justifyContent='center'
      flexShrink={0}
    >
      {isSelected &&
        (isMulti ? (
          <Check size={12} color='white' strokeWidth={3} />
        ) : (
          <Box w='8px' h='8px' borderRadius='full' bg='white' />
        ))}
    </Box>
  )
}

const useRowColors = () => {
  const focusBg = useColorModeValue('gray.50', 'whiteAlpha.100')
  const selectedBg = useColorModeValue('gray.100', 'whiteAlpha.200')
  return { focusBg, selectedBg }
}
